import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Tag, X, Calendar } from 'lucide-react'
import { supabase } from '../services/supabase'
import { formatDate } from '../utils/helpers'
import Card from './Card'

/**
 * TaggedHabitsView Component
 * Displays habits tagged for care planning from client examinations
 */
const TaggedHabitsView = ({ clientId, organizationId }) => {
  const [taggedHabits, setTaggedHabits] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (clientId && organizationId) {
      fetchTaggedHabits()
    }
  }, [clientId, organizationId])

  const fetchTaggedHabits = async () => {
    if (!clientId || !organizationId) return
    
    setLoading(true)
    try {
      // Fetch examinations for this client
      const { data: examinations, error } = await supabase
        .from('examinations')
        .select('id, created_at, medical_history')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(10) // Get last 10 examinations

      if (error) throw error

      // Extract tagged habits from medical_history
      const habits = []
      examinations?.forEach((exam) => {
        const history = exam.medical_history || {}
        
        // Check each habit category
        const categories = ['diet', 'exercise', 'sleep']
        categories.forEach((category) => {
          const categoryHabits = history[category] || []
          if (Array.isArray(categoryHabits)) {
            categoryHabits.forEach((habit) => {
              if (habit.tagged) {
                habits.push({
                  id: `${exam.id}-${category}-${habit.id}`,
                  text: habit.text,
                  category: category.charAt(0).toUpperCase() + category.slice(1),
                  examinationDate: exam.created_at,
                  examinationId: exam.id,
                })
              }
            })
          }
        })
      })

      setTaggedHabits(habits)
    } catch (error) {
      console.error('Error fetching tagged habits:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card variant="soft" className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </Card>
    )
  }

  if (taggedHabits.length === 0) {
    return (
      <Card variant="soft" className="p-6">
        <div className="text-center py-8">
          <Tag size={48} className="mx-auto text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            No habits tagged for care planning yet.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Tag habits during examination to see them here.
          </p>
        </div>
      </Card>
    )
  }

  // Group habits by category
  const groupedHabits = taggedHabits.reduce((acc, habit) => {
    if (!acc[habit.category]) {
      acc[habit.category] = []
    }
    acc[habit.category].push(habit)
    return acc
  }, {})

  return (
    <Card variant="soft" className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Tag size={20} className="text-primary-600 dark:text-primary-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Tagged Habits for Care Planning
        </h3>
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          {taggedHabits.length} total
        </span>
      </div>

      <div className="space-y-4">
        {Object.entries(groupedHabits).map(([category, habits]) => (
          <div key={category} className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {category}
            </h4>
            <div className="space-y-2">
              {habits.map((habit) => (
                <motion.div
                  key={habit.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800"
                >
                  <Tag size={16} className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {habit.text}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <Calendar size={12} />
                      <span>From examination on {formatDate(habit.examinationDate)}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default TaggedHabitsView

